import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import AuthService from '../services/AuthService';
import jwtDecode from 'jwt-decode';
import { User } from '../shared/interfaces/IUser';
import { IToken } from '../shared/interfaces/IToken';

interface Props {
  children?: ReactNode;
}

interface Auth {
  authenticated: boolean;
  login: (
    username: string,
    password: string,
    loading: Dispatch<SetStateAction<boolean>>
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  silentLogout: () => void;
  user: User | null;
  hasRole: (realmRole: string) => boolean;
  exceedsLimit: (limitName: string, testValue: number) => boolean;
}

export const AuthContext = createContext<Auth>({
  authenticated: false,
  user: null,
  login: async () => false,
  logout: async () => {},
  silentLogout: () => {},
  hasRole: () => false,
  exceedsLimit: () => false,
});

const AuthProvider: FC<Props> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getTokens = () => {
    const accessToken = localStorage.getItem('access_token') || null;
    const refreshToken = localStorage.getItem('refresh_token') || null;
    return {
      accessToken,
      refreshToken,
    };
  };

  const updateTokens = (access_token: string, refresh_token: string) => {
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
  };

  const hasRole = (realmRole: string) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (!decodedToken.realm_access) return false;
      return decodedToken.realm_access.roles.includes(realmRole);
    }

    return false;
  };

  const exceedsLimit = (limitName: string, testValue: number) => {
    const { accessToken } = getTokens();

    if (accessToken) {
      const decodedToken = jwtDecode<IToken>(accessToken);
      if (typeof decodedToken[limitName] === 'undefined') {
        return false;
      }
      const limit = decodedToken[limitName];
      return testValue > limit;
    }

    return false;
  };

  const performAuthCleanup = () => {
    if (tokenRefreshTimerRef.current) {
      clearTimeout(tokenRefreshTimerRef.current);
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAuthenticated(false);
    setUser(null);
  };

  const login = async (
    username: string,
    password: string,
    loading: Dispatch<SetStateAction<boolean>>
  ) => {
    const response = await AuthService.login(username, password, loading);

    if (typeof response === 'boolean') return response;

    const { access_token, refresh_token } = response;

    if (access_token && refresh_token) {
      updateTokens(access_token, refresh_token);
      const decodedToken = jwtDecode<IToken>(access_token);
      if (!decodedToken.exp) return false;
      const expiresIn = (decodedToken.exp - new Date().getTime() / 1000) * 1000 - 10000;
      tokenRefreshTimerRef.current = setTimeout(() => refresh(refresh_token), expiresIn);
      await updateUser(access_token);
      setAuthenticated(true);
    }

    return true;
  };

  const logout = async () => {
    const { accessToken, refreshToken } = getTokens();

    if (accessToken && refreshToken) {
      await AuthService.logout(accessToken, refreshToken);
      performAuthCleanup();
      window.location.replace(window.location.origin);
    }
  };

  const silentLogout = () => {
    const { accessToken, refreshToken } = getTokens();

    if (accessToken && refreshToken) {
      performAuthCleanup();
      window.location.replace(window.location.origin);
    }
  };

  const updateUser = async (access_token: string) => {
    const data = await AuthService.loadUserProfile(access_token);
    if (!data) return;
    setUser(data);
  };

  const refresh = useCallback(async (refreshToken: string, signal?: AbortSignal) => {
    const response = await AuthService.refreshToken(refreshToken, signal);

    if (!response) {
      performAuthCleanup();
      return;
    }

    const { access_token, refresh_token } = response;

    if (access_token && refresh_token) {
      updateTokens(access_token, refresh_token);
      const decodedToken = jwtDecode<IToken>(access_token);
      if (!decodedToken.exp) return;
      const expiresIn = (decodedToken.exp - new Date().getTime() / 1000) * 1000 - 10000;
      tokenRefreshTimerRef.current = setTimeout(() => refresh(refresh_token), expiresIn);
      await updateUser(access_token);
      setAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const { accessToken, refreshToken } = getTokens();
    const controller = new AbortController();

    if (accessToken && refreshToken) {
      (async () => await refresh(refreshToken, controller.signal))();
    }

    return () => {
      controller.abort();
    };
  }, [refresh]);

  useEffect(() => {
    const forceCleanup = (event: StorageEvent) => {
      if (
        (event.key === 'access_token' || event.key === 'refresh_token') &&
        event.oldValue &&
        !event.newValue
      ) {
        performAuthCleanup();
        window.location.replace(window.location.origin);
      }
    };

    window.addEventListener('storage', (e) => {
      forceCleanup(e);
    });

    return () => {
      window.removeEventListener('storage', (e) => {
        forceCleanup(e);
      });
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        user,
        login,
        logout,
        silentLogout,
        hasRole,
        exceedsLimit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
